import uniqBy from 'lodash/uniqBy';
import React, { Fragment, useEffect, useRef } from 'react';

import { ResizeBoundariesObserver } from 'craft/components/Resize/ResizeBoundariesObserver';
import { SetSize, Size } from 'craft/components/Size';
import { useRefresh } from 'craft/hooks/useRefresh';
import { CssSelectorId } from 'craft/types';
import { isDomElement } from 'craft/utils/viewport';

import { ResizingAgent } from './ResizingAgent';

interface Props {
  size: Size;
  doms: HTMLElement[];
  setSize: SetSize;
  nodeIds: string[];
  allowPercentages?: boolean;
  cssSelectorId?: CssSelectorId;
}

export const ResizeHandlers = React.memo(
  ({ doms, setSize, nodeIds, size, allowPercentages, cssSelectorId }: Props) => {
    const [refresh, refreshCounter] = useRefresh();
    const resizingAgentRef = useRef<ResizingAgent>();

    useEffect(() => {
      resizingAgentRef.current = new ResizingAgent(
        nodeIds,
        refresh,
        size,
        setSize,
        allowPercentages,
        cssSelectorId,
      );

      return () => {
        resizingAgentRef.current?.tearDown();
        // remove the resizing agent ref entirely
        resizingAgentRef.current = undefined;
      };
    }, [doms, nodeIds]);

    useEffect(() => {
      if (!resizingAgentRef.current) {
        return;
      }
      // you must refresh the resizing agent to use the new size
      // in case you do multiple drags
      resizingAgentRef.current.size = size;
    }, [size]);

    const boundaries = uniqBy(doms, (d) => d.getAttribute('data-nid'))
      .filter((dom) => isDomElement(dom))
      .map((dom, i) => (
        <ResizeBoundariesObserver
          key={(dom.getAttribute('data-nid') || '') + refreshCounter}
          dom={dom}
          size={size}
          showDraggingLabel={!!(resizingAgentRef?.current?.draggingStart && i === doms.length - 1)}
          resizingAgent={resizingAgentRef}
          allowPercentages={allowPercentages}
        />
      ));

    return <Fragment key={nodeIds.join('')}>{boundaries}</Fragment>;
  },
);

ResizeHandlers.displayName = 'ResizeHandlers';
